import React, { useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';

import { timezoneGroups } from '../../config/timezones';

/**
 * The timezone picker. Every surface that writes a timezone uses this one —
 * Locations, Profile, and the inline Create Branch in device edit.
 *
 * Searchable and grouped (MENA first), and crucially it offers EVERY zone the
 * backend accepts. The previous per-page pickers each listed a curated subset,
 * and a <select> given a value it has no option for reports "" — which an
 * uncontrolled form then saved back, silently wiping a timezone nobody touched.
 * `timezoneGroups(value)` also folds an unrecognised stored value in as its own
 * group, so even a retired zone id displays instead of vanishing.
 *
 * @param {string|null} value      current IANA zone
 * @param {Function}    onChange   receives the new zone (or null when unset)
 * @param {boolean}     allowUnset adds an explicit "Not set" option
 * @param {string}      placeholder
 */
export default function TimezonePicker({
    value,
    onChange,
    allowUnset = false,
    placeholder,
    className = 'w-100',
    disabled = false,
    id,
}) {
    const { t } = useTranslation();

    const groups = useMemo(() => {
        const g = timezoneGroups(value);
        if (!allowUnset) return g;
        return [
            { label: '', items: [{ label: t('timezone.not_set', 'Not set'), value: '__unset__' }] },
            ...g,
        ];
    }, [value, allowUnset, t]);

    return (
        <Dropdown
            id={id}
            className={className}
            value={value ?? (allowUnset ? '__unset__' : null)}
            options={groups}
            optionGroupLabel="label"
            optionGroupChildren="items"
            optionLabel="label"
            optionValue="value"
            onChange={(e) => onChange(e.value === '__unset__' ? null : e.value)}
            filter
            filterBy="label"
            resetFilterOnHide
            disabled={disabled}
            placeholder={placeholder || t('timezone.placeholder', 'Search a timezone…')}
            emptyFilterMessage={t('timezone.no_match', 'No matching timezone')}
        />
    );
}
